import { useEffect, useState } from 'react';
import {
  NOTIFICATION_KINDS, NOTIFICATION_KIND_LABELS, NOTIFICATION_MODES,
  type ID, type NotificationKind, type NotificationMode, type NotificationPrefs, type NotificationSettings,
} from '@proofroom/schema';
import { Header } from '../components/Header';
import { Switch } from '../components/Switch';
import { Spinner } from '../components/ui';
import { notifications, push } from '../api/endpoints';
import { ApiError } from '../api/client';

const MODE_LABELS: Record<NotificationMode, string> = {
  off: 'Never',
  immediate: 'Straight away',
  digest: 'Hourly digest',
};

const message = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

/** Push needs all three: the worker to receive it, the manager to subscribe, the permission to show it. */
const pushSupported = (): boolean => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** The VAPID key travels as base64url; PushManager.subscribe wants the bytes, over a plain ArrayBuffer. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=').replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * The rows come from NOTIFICATION_KINDS, so a kind added on the server appears here without a
 * change to this file. Save sends the whole map, which is what the server takes. The master
 * switch and the project switches save the moment they are flipped: `settings` is what the
 * server last answered, `prefs` the grid's working copy until Save.
 */
export function Notifications() {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [masterBusy, setMasterBusy] = useState(false);
  const [masterError, setMasterError] = useState<string | null>(null);
  const [projectBusy, setProjectBusy] = useState<ID | null>(null);
  const [projectSaved, setProjectSaved] = useState<ID | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  // Push, for this browser alone: null key means it is not on offer (unsupported here, or the
  // server has no VAPID keys), and the registration is kept so Allow and Turn off share it.
  const [pushKey, setPushKey] = useState<string | null>(null);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [pushOn, setPushOn] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const loaded = await notifications.settings();
        setSettings(loaded);
        setPrefs(loaded.prefs);
      } catch (err) {
        setLoadError(message(err, 'Could not load your notification settings'));
      }
    })();
  }, []);

  // Registering the worker is what a browser that can be pushed to needs; a browser that cannot,
  // or a server with no keys, is never asked and never told about it.
  useEffect(() => {
    if (!pushSupported()) return;
    void (async () => {
      try {
        const key = await push.vapidKey();
        if (!key.enabled || !key.publicKey) return;
        const reg = await navigator.serviceWorker.register('/sw.js');
        setRegistration(reg);
        setPushKey(key.publicKey);
        setPermission(Notification.permission);
        setPushOn(Boolean(await reg.pushManager.getSubscription()));
      } catch (err) {
        setPushError(message(err, 'Could not set up notifications in this browser'));
      }
    })();
  }, []);

  const allowPush = async () => {
    if (!registration || !pushKey) return;
    setPushBusy(true);
    setPushError(null);
    try {
      const granted = await Notification.requestPermission();
      setPermission(granted);
      if (granted !== 'granted') return;
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(pushKey) });
      const { endpoint, keys } = subscription.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } };
      if (!endpoint || !keys) throw new Error('this browser gave no usable subscription');
      await push.subscribe({ endpoint, keys });
      setPushOn(true);
    } catch (err) {
      setPushError(message(err, 'Could not turn on notifications for this browser'));
    } finally {
      setPushBusy(false);
    }
  };

  const stopPush = async () => {
    if (!registration) return;
    setPushBusy(true);
    setPushError(null);
    try {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        await push.unsubscribe(subscription.endpoint);
      }
      setPushOn(false);
    } catch (err) {
      setPushError(message(err, 'Could not turn off notifications for this browser'));
    } finally {
      setPushBusy(false);
    }
  };

  const choose = (kind: NotificationKind, mode: NotificationMode) => {
    setPrefs((prev) => (prev ? { ...prev, [kind]: mode } : prev));
    setSaved(false);
    setSaveError(null);
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    setSaveError(null);
    try {
      const next = await notifications.save({ prefs });
      setSettings(next);
      setPrefs(next.prefs);
      setSaved(true);
    } catch (err) {
      setSaveError(message(err, 'Could not save your notification settings'));
    } finally {
      setSaving(false);
    }
  };

  // The grid's working copy is left alone: a flip of the master switch is not a Save
  const setMaster = async (emailEnabled: boolean) => {
    setMasterBusy(true);
    setMasterError(null);
    try {
      setSettings(await notifications.save({ emailEnabled }));
    } catch (err) {
      setMasterError(message(err, 'Could not change the switch'));
    } finally {
      setMasterBusy(false);
    }
  };

  const setProject = async (projectId: ID, on: boolean) => {
    setProjectBusy(projectId);
    setProjectSaved(null);
    setProjectError(null);
    try {
      setSettings(await notifications.setProjectMuted(projectId, !on));
      setProjectSaved(projectId);
    } catch (err) {
      setProjectError(message(err, 'Could not change the switch'));
    } finally {
      setProjectBusy(null);
    }
  };

  const emailOn = settings?.emailEnabled ?? true;

  return (
    <div className="app">
      <Header crumbs={[{ label: 'Projects', to: '/projects' }, { label: 'Notifications' }]} />
      <main className="main">
        <div className="page" style={{ maxWidth: 720 }}>
          <div className="page-head"><h1>Notifications</h1></div>
          <p className="muted small">Emails go to the address you sign in with. A digest gathers everything waiting and arrives once an hour.</p>

          {loadError && <p className="error-text" role="alert">{loadError}</p>}
          {!loadError && !(settings && prefs) && <Spinner label="Loading your notification settings" />}
          {settings && prefs && (
            <>
              <div className="card" style={{ padding: '6px 16px', marginBottom: 16 }}>
                <div className="switch-row">
                  <div>
                    <div>Send me email alerts</div>
                    {!emailOn && <div className="muted small">No email will be sent while this is off. Your choices below are kept.</div>}
                  </div>
                  <Switch label="Send me email alerts" checked={emailOn} busy={masterBusy} onChange={(on) => void setMaster(on)} />
                </div>
                {masterError && <p className="error-text small" role="alert">{masterError}</p>}
              </div>

              <div className="card" style={{ overflow: 'auto', opacity: emailOn ? 1 : 0.55 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Email me when</th>
                      {NOTIFICATION_MODES.map((mode) => <th key={mode}>{MODE_LABELS[mode]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {NOTIFICATION_KINDS.map((kind) => (
                      <tr key={kind}>
                        <td>{NOTIFICATION_KIND_LABELS[kind]}</td>
                        {NOTIFICATION_MODES.map((mode) => (
                          <td key={mode}>
                            <input
                              type="radio"
                              name={kind}
                              value={mode}
                              checked={prefs[kind] === mode}
                              disabled={!emailOn}
                              aria-label={`${NOTIFICATION_KIND_LABELS[kind]}: ${MODE_LABELS[mode]}`}
                              onChange={() => choose(kind, mode)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="btn-row" style={{ marginTop: 16, alignItems: 'center' }}>
                <button className="btn primary" onClick={() => void save()} disabled={saving || !emailOn}>{saving ? 'Saving' : 'Save'}</button>
                {saved && <span className="muted small">Saved</span>}
              </div>
              {saveError && <p className="error-text" role="alert">{saveError}</p>}

              <div className="card" aria-label="Projects" style={{ padding: '6px 16px', marginTop: 20 }}>
                <h2 style={{ marginTop: 10 }}>Projects</h2>
                <p className="muted small">Turn a project off to hear nothing about it by email. Each switch saves as you flip it.</p>
                {settings.projects.length === 0 && <p className="muted small">You are not on any projects yet.</p>}
                {settings.projects.map((p) => (
                  <div key={p.projectId} className="switch-row">
                    <div>
                      <span>{p.name}</span>
                      {projectSaved === p.projectId && <span className="muted small" style={{ marginLeft: 8 }}>Saved</span>}
                    </div>
                    <Switch
                      label={`Email alerts for ${p.name}`}
                      checked={!p.muted}
                      busy={projectBusy === p.projectId}
                      onChange={(on) => void setProject(p.projectId, on)}
                    />
                  </div>
                ))}
                {projectError && <p className="error-text small" role="alert">{projectError}</p>}
              </div>
            </>
          )}

          {pushKey && (
            <div className="card" aria-label="This browser" style={{ padding: 16, marginTop: 20 }}>
              <h2 style={{ marginTop: 0 }}>This browser</h2>
              <p className="muted small">
                A notification on this device for everything you have set to arrive straight away, whether or not Proofroom is open.
                Each device is asked separately.
              </p>
              {pushOn ? (
                <div className="btn-row" style={{ alignItems: 'center' }}>
                  <span>Notifications are on for this browser</span>
                  <button className="btn" onClick={() => void stopPush()} disabled={pushBusy}>Turn off</button>
                </div>
              ) : permission === 'denied' ? (
                <p className="muted small">This browser is blocking notifications for Proofroom. Allow them in the browser's own settings for this site, then come back.</p>
              ) : (
                <button className="btn" onClick={() => void allowPush()} disabled={pushBusy}>Allow notifications</button>
              )}
            </div>
          )}
          {/* Outside the card: a registration that threw leaves no card to put the message in */}
          {pushError && <p className="error-text" role="alert">{pushError}</p>}
        </div>
      </main>
    </div>
  );
}
